import { Flex, chakra, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import { useScrollDirection } from 'lib/contexts/scrollDirection';
import useIsSticky from 'lib/hooks/useIsSticky';

type Props = {
  children: React.ReactNode;
  className?: string;
  showShadow?: boolean;
  topOffset?: number;
  stickySurface?: boolean;
};

const TOP_UP = 106;
const TOP_DOWN = 64;
export const ACTION_BAR_HEIGHT_DESKTOP = 24 + 32 + 12;
export const ACTION_BAR_HEIGHT_MOBILE = 24 + 32 + 24;

const ActionBar = ({ children, className, showShadow, topOffset, stickySurface }: Props) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const scrollDirection = useScrollDirection();
  const isSticky = useIsSticky(ref, TOP_UP + 5);
  const stickyTopOffset = topOffset ?? TOP_UP;
  const bgColor = useColorModeValue('white', 'black');
  const stickySurfaceShadow = `0 0 0 12px ${ bgColor }`;
  const stickySurfaceShadowWithDepth = `${ stickySurfaceShadow }, var(--chakra-shadows-md)`;
  let stickyDesktopShadow = stickySurfaceShadow;

  if (showShadow) {
    stickyDesktopShadow = `${ stickySurfaceShadow }, var(--chakra-shadows-action_bar)`;
  }

  const baseShadow = stickySurface ? stickySurfaceShadowWithDepth : 'md';
  let desktopShadow = 'none';

  if (stickySurface) {
    desktopShadow = stickyDesktopShadow;
  } else if (showShadow) {
    desktopShadow = 'action_bar';
  }

  if (!React.Children.toArray(children).filter(Boolean).length) {
    return null;
  }

  return (
    <Flex
      className={ className }
      bgColor={ stickySurface ? bgColor : undefined }
      pb={{ base: 6, lg: 0 }}
      mx={{ base: -3, lg: 0 }}
      px={{ base: 3, lg: 0 }}
      justifyContent="space-between"
      width={{ base: '100%', lg: 'unset' }}
      position="sticky"
      top={{ base: scrollDirection === 'down' ? `${ TOP_DOWN }px` : `${ stickyTopOffset }px`, lg: 0 }}
      transitionProperty="top,box-shadow,background-color,color"
      transitionDuration="normal"
      zIndex={{ base: 'sticky2', lg: 'docked' }}
      boxShadow={{
        base: isSticky ? baseShadow : 'none',
        lg: isSticky ? desktopShadow : 'none',
      }}
      ref={ ref }
    >
      { children }
    </Flex>
  );
};

export default chakra(ActionBar);
